import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import AutomaticChargeForm from './AutomaticChargeForm';
import SummaryModal from './SummaryModal';
import EstudiantesPreviewTable from './EstudiantesPreviewTable';
import styles from '../../styles/GeneracionCargosAutomaticosStyles/GeneracionCargosAutomaticos.module.css';
import { 
  getCampusDropdown, 
  getConceptosActivos, 
  getCiclosEscolares,
  getSeccionesDropdown,
  generarCargosAutomaticos,
  getEstudiantesElegibles,
  generarCSVPrevio
} from '../../api/cargosAutomaticos';

function GeneracionCargosAutomaticosScreen() {
  const [showSummary, setShowSummary] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState(null);
  
  // Estado para preview de estudiantes
  const [showPreview, setShowPreview] = useState(false);
  const [estudiantesPreview, setEstudiantesPreview] = useState([]);
  const [previewStats, setPreviewStats] = useState({ 
    total: 0, 
    duplicados: 0, 
    conBeca: 0,
    montoOriginal: 0,
    montoFinal: 0,
    ahorroPorBecas: 0 
  });
  const [conceptoAplicaBecas, setConceptoAplicaBecas] = useState(false);
  
  // Opciones para dropdowns
  const [campusOptions, setCampusOptions] = useState([]);
  const [conceptosOptions, setConceptosOptions] = useState([]);
  const [ciclosOptions, setCiclosOptions] = useState([]);
  const [seccionesOptions, setSeccionesOptions] = useState([]);
  
  // Formulario
  const [formData, setFormData] = useState({
    cicloEscolar: '',
    campus: '',
    conceptoCobro: '',
    seccion: '', // Opcional
    fechaAplicacion: '',
    fechaVencimiento: '',
    monto: '',
    descripcion: ''
  });

  // Cargar opciones al montar el componente
  useEffect(() => {
    loadInitialData();
  }, []);

  // Cargar conceptos cuando cambie el campus o el ciclo escolar seleccionado.
  // Regla: el concepto se filtra por (campus + ciclo). Si falta cualquiera de los dos
  // no se cargan conceptos para evitar mostrar conceptos de otros ciclos/campus.
  useEffect(() => {
    if (formData.campus && formData.cicloEscolar) {
      const cicloObj = ciclosOptions.find(
        (c) => String(c.id_ciclo_escolar) === String(formData.cicloEscolar)
      );
      const cicloNombre = cicloObj?.ciclo || cicloObj?.nombre_ciclo || '';
      loadConceptos(formData.campus, cicloNombre, formData.cicloEscolar);
      // Resetear concepto seleccionado al cambiar de campus o ciclo
      setFormData(prev => ({ ...prev, conceptoCobro: '' }));
      setShowPreview(false);
      setEstudiantesPreview([]);
    } else {
      // Si falta campus o ciclo, limpiar conceptos
      setConceptosOptions([]);
      setFormData(prev => (prev.conceptoCobro ? { ...prev, conceptoCobro: '' } : prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.campus, formData.cicloEscolar, ciclosOptions]);

  // Cargar secciones cuando cambia el campus
  useEffect(() => {
    if (formData.campus) {
      loadSecciones(formData.campus);
    }
  }, [formData.campus]);

  const loadInitialData = async () => {
    try {
      // Cargar campus
      const campusResponse = await getCampusDropdown();
      console.log('Campus cargados:', campusResponse);
      setCampusOptions(campusResponse || []);
      
      // Cargar ciclos escolares
      const ciclosResponse = await getCiclosEscolares();
      console.log('Ciclos cargados:', ciclosResponse);
      setCiclosOptions(ciclosResponse || []);
      
      // Seleccionar primer campus y ciclo por defecto
      if (campusResponse && campusResponse.length > 0) {
        setFormData(prev => ({ ...prev, campus: campusResponse[0].id }));
      }
      
      if (ciclosResponse && ciclosResponse.length > 0) {
        setFormData(prev => ({ ...prev, cicloEscolar: ciclosResponse[0].id_ciclo_escolar }));
      }
    } catch (err) {
      console.error('Error al cargar datos iniciales:', err);
      setError('No se pudieron cargar los datos iniciales. Por favor recargue la página.');
    }
  };

  const loadConceptos = async (campusId, cicloNombre = '', cicloId = '') => {
    try {
      const response = await getConceptosActivos({ campusId, cicloNombre, cicloId });
      console.log('Conceptos cargados para campus', campusId, 'ciclo', cicloNombre, '(id', cicloId, '):', response);
      if (response.success) {
        setConceptosOptions(response.conceptos || []);
      }
    } catch (err) {
      console.error('Error al cargar conceptos:', err);
      setConceptosOptions([]);
    }
  };

  const loadSecciones = async (campusId) => {
    try {
      const response = await getSeccionesDropdown(campusId);
      setSeccionesOptions(response || []);
    } catch (err) {
      console.error('Error al cargar secciones:', err);
      setError('No se pudieron cargar las secciones');
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
    
    // Limpiar preview cuando cambie el concepto de cobro o sección (los filtros principales)
    if (field === 'conceptoCobro' || field === 'seccion') {
      setShowPreview(false);
      setEstudiantesPreview([]);
      setPreviewStats({ 
        total: 0, 
        duplicados: 0, 
        conBeca: 0,
        montoOriginal: 0,
        montoFinal: 0,
        ahorroPorBecas: 0 
      });
    }
  };

  const validateForm = () => {
    const errors = [];
    
    // Ciclo escolar ya no es obligatorio - solo es informativo
    if (!formData.campus) errors.push('Debe seleccionar un campus');
    if (!formData.conceptoCobro) errors.push('Debe seleccionar un concepto de cobro');
    if (!formData.fechaAplicacion) errors.push('Debe definir la fecha de aplicación');
    if (!formData.fechaVencimiento) errors.push('Debe definir la fecha de vencimiento');
    
    // Validar que fecha de vencimiento sea posterior a fecha de aplicación
    if (formData.fechaAplicacion && formData.fechaVencimiento) {
      if (new Date(formData.fechaVencimiento) < new Date(formData.fechaAplicacion)) {
        errors.push('La fecha de vencimiento debe ser posterior a la fecha de aplicación');
      }
    }
    
    return errors;
  };

  // Nueva función para obtener preview de estudiantes
  const handlePreviewEstudiantes = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      setError(errors.join('. '));
      return;
    }
    
    setLoadingPreview(true);
    setError(null);
    
    try {
      const response = await getEstudiantesElegibles(formData);
      
      if (response.success) {
        setEstudiantesPreview(response.estudiantes || []);
        
        // Contar estudiantes con beca
        const estudiantesConBeca = (response.estudiantes || []).filter(e => e.tiene_beca).length;
        
        setPreviewStats({
          total: response.total || 0,
          duplicados: response.duplicados || 0,
          conBeca: response.total_con_beca || estudiantesConBeca,
          montoOriginal: response.monto_total_original || 0,
          montoFinal: response.monto_total || 0,
          ahorroPorBecas: response.ahorro_por_becas || 0
        });
        
        // Actualizar si el concepto aplica becas
        setConceptoAplicaBecas(response.concepto_aplica_becas || false);
        
        setShowPreview(true);
      } else {
        setError(response.message || 'Error al obtener estudiantes elegibles');
      }
    } catch (err) {
      console.error('Error al obtener preview:', err);
      setError(err.message || 'Error al obtener estudiantes elegibles');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Función para descargar CSV previo a la aplicación
  const handleDownloadCSV = () => {
    if (estudiantesPreview.length === 0) {
      setError('No hay estudiantes para exportar');
      return;
    }
    
    const conceptoSeleccionado = conceptosOptions.find(
      c => c.id === parseInt(formData.conceptoCobro)
    );
    
    generarCSVPrevio(
      estudiantesPreview, 
      formData, 
      conceptoSeleccionado?.nombre || 'Concepto',
      formData.monto || conceptoSeleccionado?.monto_default || 0
    );
  };

  const handleGenerateCargos = async () => {
    // Validar que se haya hecho el preview primero
    if (!showPreview || estudiantesPreview.length === 0) {
      setError('Debe realizar la Vista Previa antes de generar los cargos');
      return;
    }
    
    // Validar que se haya seleccionado un ciclo escolar (requerido para asociar al cargo)
    if (!formData.cicloEscolar) {
      setError('Debe seleccionar un Ciclo Escolar para asociar los cargos');
      return;
    }
    
    // ============================================================
    // VALIDACIÓN DE DATOS EN PREVISUALIZACIÓN
    // ============================================================
    const erroresValidacion = [];
    
    // Filtrar estudiantes elegibles (excluir duplicados)
    const estudiantesElegibles = estudiantesPreview.filter(e => e.estado !== 'DUPLICADO');
    
    if (estudiantesElegibles.length === 0) {
      setError('No hay estudiantes elegibles para generar cargos. Todos los estudiantes ya tienen cargos para este concepto.');
      return;
    }
    
    // Validar que todos los estudiantes tengan datos completos
    const estudiantesConDatosIncompletos = estudiantesElegibles.filter(e => {
      return !e.id_estudiante || 
             !e.matricula || 
             e.monto_final === null || 
             e.monto_final === undefined ||
             isNaN(e.monto_final);
    });
    
    if (estudiantesConDatosIncompletos.length > 0) {
      erroresValidacion.push(`${estudiantesConDatosIncompletos.length} estudiante(s) tienen datos incompletos (ID, matrícula o monto faltante)`);
    }
    
    // Validar que no haya montos negativos
    const estudiantesConMontoNegativo = estudiantesElegibles.filter(e => e.monto_final < 0);
    if (estudiantesConMontoNegativo.length > 0) {
      erroresValidacion.push(`${estudiantesConMontoNegativo.length} estudiante(s) tienen monto final negativo`);
    }
    
    // Validar que no haya registros duplicados en el preview (por id_estudiante)
    const idsEstudiantes = estudiantesElegibles.map(e => e.id_estudiante);
    const idsUnicos = new Set(idsEstudiantes);
    if (idsUnicos.size !== idsEstudiantes.length) {
      erroresValidacion.push('Se detectaron estudiantes duplicados en la previsualización');
    }
    
    // Validar concepto seleccionado
    const conceptoSeleccionado = conceptosOptions.find(
      c => c.id === parseInt(formData.conceptoCobro)
    );
    
    if (!conceptoSeleccionado) {
      erroresValidacion.push('El concepto de cobro seleccionado no es válido');
    }
    
    // Si hay errores de validación, mostrar y bloquear
    if (erroresValidacion.length > 0) {
      setError('Errores de validación detectados:\n' + erroresValidacion.join('\n'));
      return;
    }
    
    // Confirmación del usuario con resumen detallado
    const confirmacion = window.confirm(
      `¿Está seguro de generar ${estudiantesElegibles.length} cargos automáticos?\n\n` +
      `Concepto: ${conceptoSeleccionado?.nombre || 'N/A'}\n` +
      `Campus: ${campusOptions.find(c => c.id === parseInt(formData.campus))?.nombre || 'N/A'}\n` +
      `Monto total: $${previewStats.montoFinal?.toLocaleString('es-MX', {minimumFractionDigits: 2}) || '0.00'}\n\n` +
      `Se recomienda descargar el CSV antes de continuar.`
    );
    
    if (!confirmacion) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Preparar datos para enviar - incluir solo IDs de estudiantes elegibles (excluir duplicados)
      const payload = {
        ...formData,
        monto: formData.monto || conceptoSeleccionado?.monto_default || 0,
        estudiantesIds: estudiantesElegibles.map(e => e.id_estudiante)
      };
      
      // Llamar API para generar cargos automáticos
      const response = await generarCargosAutomaticos(payload);
      
      if (response.success) {
        // Mostrar resumen
        setSummaryData({
          ...response,
          formData: {
            ciclo: ciclosOptions.find(c => c.id_ciclo_escolar === parseInt(formData.cicloEscolar))?.ciclo || '',
            campus: campusOptions.find(c => c.id === parseInt(formData.campus))?.nombre || '',
            concepto: conceptosOptions.find(c => c.id === parseInt(formData.conceptoCobro))?.nombre || '',
            seccion: formData.seccion ? seccionesOptions.find(s => s.id === parseInt(formData.seccion))?.nombre || 'Todas' : 'Todas',
            fechaAplicacion: formData.fechaAplicacion,
            fechaVencimiento: formData.fechaVencimiento,
            monto: payload.monto
          }
        });
        setShowSummary(true);
        // Limpiar preview después de generar
        setShowPreview(false);
        setEstudiantesPreview([]);
      } else {
        setError(response.message || 'Error al generar cargos automáticos');
      }
    } catch (err) {
      console.error('Error al generar cargos:', err);
      setError(err.message || 'Error al generar cargos automáticos. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Resetear formulario y preview
    setFormData({
      cicloEscolar: ciclosOptions.length > 0 ? ciclosOptions[0].id_ciclo_escolar : '',
      campus: campusOptions.length > 0 ? campusOptions[0].id : '',
      conceptoCobro: '',
      seccion: '',
      fechaAplicacion: '',
      fechaVencimiento: '',
      monto: '',
      descripcion: ''
    });
    setError(null);
    setShowPreview(false);
    setEstudiantesPreview([]);
    setPreviewStats({ 
      total: 0, 
      duplicados: 0, 
      conBeca: 0,
      montoOriginal: 0,
      montoFinal: 0,
      ahorroPorBecas: 0 
    });
    setConceptoAplicaBecas(false);
  };

  const handleCloseSummary = () => {
    setShowSummary(false);
    setSummaryData(null);
    // Resetear formulario después de cerrar el resumen
    handleCancel();
  };

  return (
    <div className={styles.layout} style={{height: '100vh', overflow: 'hidden'}}>
      <div className={styles.sidebarColumn} style={{height: '100vh', position: 'sticky', top: 0, zIndex: 10}}>
        <Sidebar />
      </div>
      <div className={styles.contentColumn} style={{height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
        <div style={{flexShrink: 0, zIndex: 11}}>
          <HeaderGlobal title="Generación de Cargos Automáticos" />
        </div>
        <div className={styles.contentArea} style={{overflowY: 'auto', height: '100%'}}>
          
          {error && (
            <div className={styles.errorMessage}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>{error}</span>
            </div>
          )}
          
          <AutomaticChargeForm 
            formData={formData}
            campusOptions={campusOptions}
            conceptosOptions={conceptosOptions}
            ciclosOptions={ciclosOptions}
            seccionesOptions={seccionesOptions}
            onFormChange={handleFormChange}
            onPreview={handlePreviewEstudiantes}
            onGenerate={handleGenerateCargos}
            onCancel={handleCancel}
            onDownloadCSV={handleDownloadCSV}
            loading={loading}
            loadingPreview={loadingPreview}
            showPreview={showPreview}
            previewCount={estudiantesPreview.length}
          />
          
          {/* Tabla de preview de estudiantes */}
          {showPreview && estudiantesPreview.length > 0 && (
            <EstudiantesPreviewTable 
              estudiantes={estudiantesPreview}
              stats={previewStats}
              onDownloadCSV={handleDownloadCSV}
              conceptoAplicaBecas={conceptoAplicaBecas}
            />
          )}
        </div>
      </div>
      
      {showSummary && summaryData && (
        <SummaryModal 
          data={summaryData}
          onClose={handleCloseSummary}
        />
      )}
    </div>
  );
}

export default GeneracionCargosAutomaticosScreen;
